import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '~/components/Button';
import { showToast } from '~/components/Toast';
import { DetailPageError } from '~/modules/errors/components/DetailPageError';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { CTABlock } from '~/modules/settings/components/CTABlock';
import { SettingsPageHeader } from '~/modules/settings/components/SettingsPageHeader';
import { SETTINGS_ROUTES } from '~/modules/settings/config/routes';
import type { UserDetail } from '~settings/api/lib/schemas';
import { useEditUser } from '~settings/api/mutations/userEdit';
import { useUser } from '~settings/api/queries/user';
import type { UserFormSchema } from '~settings/modules/user/components/UserForm';
import {
  mapApiDataToForm,
  mapFormDataToApi,
  UserForm,
} from '~settings/modules/user/components/UserForm';
import { getMessageFromStatus } from '~settings/modules/user/helpers';

const FORM_ID = 'editUserForm';

export function EditUserPage() {
  const { t } = useTranslation('settings');
  const { userId } = useParams();
  const navigate = useNavigate();

  const [isInternalUpdate] = useFeatureFlags([
    FEATURE_FLAGS.SettingsInternalUserUpdates,
  ]);

  const { mutate: submitData, isPending } = useEditUser(userId ?? '', {
    onSuccess: (data: UserDetail) => {
      navigate(SETTINGS_ROUTES.USER_DETAIL(data.id));
    },
    onError: (error) => {
      const status = error?.response?.status;
      const message = getMessageFromStatus(status, true);

      showToast({
        title: t(message.title),
        description: t(message.description),
      });
    },
  });
  const { isLoading, isFetching, error, user } = useUser(
    {
      userId: userId || '',
      withRolesMap: true,
    },
    {
      staleTime: 0,
    },
  );

  const userValues = useMemo<UserFormSchema | null>(
    () => (user ? mapApiDataToForm(user, isInternalUpdate) : null),
    [user, isInternalUpdate],
  );

  if (isLoading || isFetching) {
    return <section className="py-md">{t('loading')}</section>;
  }

  if (error || !user || !userValues || !userId) {
    return (
      <DetailPageError
        errorCode={error?.response?.status}
        fallbackRoute={{
          href: SETTINGS_ROUTES.ROOT,
          label: t('backToUserManagement'),
        }}
      />
    );
  }

  const onSubmit = (data: UserFormSchema) => {
    const apiData = mapFormDataToApi(data, isInternalUpdate);

    submitData(apiData);
  };

  return (
    <article className="relative flex min-h-full flex-col">
      <SettingsPageHeader
        breadcrumbItems={[
          { to: SETTINGS_ROUTES.ROOT, label: 'title' },
          { to: SETTINGS_ROUTES.USER_LIST, label: 'users' },
          { to: '', label: 'user.editUserTitle' },
        ]}
        title="user.editUserTitle"
      />
      <div className="grow overflow-y-auto pb-xl">
        <UserForm
          onSubmit={onSubmit}
          id={FORM_ID}
          values={userValues}
          onError={(e) => {
            console.error(e);
          }}
        />
      </div>
      <CTABlock>
        <Button asChild variant="secondary" size="large">
          <Link to={SETTINGS_ROUTES.USER_DETAIL(userId)}>
            {t('buttons.cancel')}
          </Link>
        </Button>
        <Button
          variant="primary"
          type="submit"
          size="large"
          form={FORM_ID}
          disabled={isPending}
        >
          {t('buttons.saveChanges')}
        </Button>
      </CTABlock>
    </article>
  );
}

EditUserPage.displayName = 'EditUserPage';
